import {
  IBasicPlayerInformation,
  IPlayerInformation,
  IPlayerStatsForTimePeriod,
  IPlayerTripHistory,
  IHotelBookingCalendar,
  IHotelRoomBookings,
  IShift4Info,
  IPlayerSearchInfo,
  IPlayerNote,
  IPlayerComp,
  IPlayerOffer,
  ICreateNote,
  IHotelBookingGuest,
  IHotelBookingSummary,
  IPlayerCreditTransaction,
  IPlayerTrip,
  IBasicPlayerNote,
  IHotelBookingPayment,
  IBasicReservation,
  IReservation,
  TaskStatus,
  IPlayerSourceOfFunds,
} from "services/models";
import { IPlayerApiService } from "services/models/ApiService";
import { IPlayerFolio } from "services/models/Player/PlayerFolio";
import { IClubSerranoInfo, IPlayerMobileAppInformation } from "services/models/Player/PlayerMobileAppInformation";
import { IAbortablePromise } from "utils";
import { ApiService } from "./ApiService";
import { apiNotConnected, authorizedAbortableFetchAsJson, authorizedFetch, authorizedFetchAsJson } from "./misc";
import { BASE_URL } from "authConfig";

export class PlayerApiService implements IPlayerApiService {
  constructor(private getAccessToken: () => Promise<string>) {}

  search(term: string | undefined): Promise<IPlayerSearchInfo[]> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/players/search`, this.getAccessToken, {
      method: "POST",
      body: JSON.stringify({ term }),
      headers: { "Content-Type": "application/json" },
    });
  }

  getBasicPlayerInformation(playerId: string): Promise<IBasicPlayerInformation> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/players/${playerId}/basicinfo`, this.getAccessToken);
  }

  getPlayerInformation(playerId: string): Promise<IPlayerInformation> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/players/${playerId}`, this.getAccessToken);
  }

  getStatsForTimePeriod(playerId: string, period: string): IAbortablePromise<IPlayerStatsForTimePeriod> {
    const url = new URL(`${ApiService.v1Url}/players/${playerId}/stats`);
    url.search = new URLSearchParams({ period }).toString();
    return authorizedAbortableFetchAsJson(url, this.getAccessToken);
  }

  getTripHistoryForTimePeriod(playerId: string, period: string): IAbortablePromise<IPlayerTripHistory[]> {
    const url = new URL(`${ApiService.v1Url}/players/${playerId}/stats/trips/graph`);
    url.search = new URLSearchParams({ period }).toString();
    return authorizedAbortableFetchAsJson(url, this.getAccessToken);
  }

  getHotelBookingCalendar(playerId: string): Promise<IHotelBookingCalendar> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/players/${playerId}/hotel/booking/calendar`, this.getAccessToken);
  }

  getRooms(playerId: string, start: string, end: string): Promise<IHotelRoomBookings> {
    return authorizedFetchAsJson(
      `${ApiService.v1Url}/players/${playerId}/hotel/booking/roomselect`,
      this.getAccessToken,
      {
        method: "POST",
        body: JSON.stringify({ checkIn: start, checkOut: end }),
        headers: { "Content-Type": "application/json" },
      }
    );
  }

  getSummary(bookingId: string): Promise<IHotelBookingSummary> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/hotel/booking/${bookingId}/summary`, this.getAccessToken);
  }

  getShift4Info(bookingId: string): Promise<IShift4Info> {
    return authorizedFetchAsJson(
      `${ApiService.v1Url}/hotel/booking/${bookingId}/paymentprovider/shift4`,
      this.getAccessToken
    );
  }

  addGuestToBooking(bookingId: string, guest: IHotelBookingGuest): Promise<IHotelBookingGuest> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/hotel/booking/${bookingId}/guests`, this.getAccessToken, {
      method: "POST",
      body: JSON.stringify(guest),
      headers: { "Content-Type": "application/json" },
    });
  }

  removeGuestFromBooking(bookingId: string, guestId: string): Promise<Response> {
    return authorizedFetch(`${ApiService.v1Url}/hotel/booking/${bookingId}/guests/${guestId}`, this.getAccessToken, {
      method: "DELETE",
    });
  }

  editGuestOnBooking(bookingId: string, guest: IHotelBookingGuest): Promise<IHotelBookingGuest> {
    return authorizedFetchAsJson(
      `${ApiService.v1Url}/hotel/booking/${bookingId}/guests/${guest.guestId}`,
      this.getAccessToken,
      {
        method: "PUT",
        body: JSON.stringify(guest),
        headers: { "Content-Type": "application/json" },
      }
    );
  }

  updateBookingPaymentRequired(bookingId: string, required: boolean): Promise<Response> {
    return authorizedFetch(`${ApiService.v1Url}/hotel/booking/${bookingId}/paymentrequired`, this.getAccessToken, {
      method: "POST",
      body: JSON.stringify({ paymentRequired: required }),
      headers: { "Content-Type": "application/json" },
    });
  }

  updateBookingPayment(bookingId: string, payment: IHotelBookingPayment): Promise<Response> {
    return authorizedFetch(`${ApiService.v1Url}/hotel/booking/${bookingId}/payment`, this.getAccessToken, {
      method: "POST",
      body: JSON.stringify(payment),
      headers: { "Content-Type": "application/json" },
    });
  }
  updateBookingPreferences(bookingId: string, codes: string[]): Promise<Response> {
    return authorizedFetch(`${ApiService.v1Url}/hotel/booking/${bookingId}/preferences`, this.getAccessToken, {
      method: "POST",
      body: JSON.stringify({
        preferenceIds: codes,
      }),
      headers: { "Content-Type": "application/json" },
    });
  }

  updateBookingNotes(bookingId: string, note: string): Promise<Response> {
    return authorizedFetch(`${ApiService.v1Url}/hotel/booking/${bookingId}/notes`, this.getAccessToken, {
      method: "POST",
      body: JSON.stringify({
        note,
      }),
      headers: { "Content-Type": "application/json" },
    });
  }

  getBookingStatus(taskPath: string): Promise<TaskStatus> {
    return authorizedFetchAsJson(`${BASE_URL}/${taskPath}`, this.getAccessToken);
  }

  getNotes(playerId: string): IAbortablePromise<IBasicPlayerNote[]> {
    return authorizedAbortableFetchAsJson(`${ApiService.v1Url}/players/${playerId}/notes`, this.getAccessToken, {
      headers: ApiService.playerNotesHeader,
    });
  }

  getNote(noteId: number): Promise<IPlayerNote> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/notes/${noteId}`, this.getAccessToken, {
      headers: ApiService.playerNotesHeader,
    });
  }

  addNote(note: ICreateNote): Promise<IPlayerNote> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/notes`, this.getAccessToken, {
      method: "POST",
      headers: {
        ...ApiService.playerNotesHeader,
        ...{ "Content-Type": "application/json" },
      },
      body: JSON.stringify(note),
    });
  }

  editNote(note: IPlayerNote): Promise<IPlayerNote> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/notes`, this.getAccessToken, {
      method: "PUT",
      headers: {
        ...ApiService.playerNotesHeader,
        ...{ "Content-Type": "application/json" },
      },
      body: JSON.stringify(note),
    });
  }

  getTrips(playerId: string): IAbortablePromise<IPlayerTrip[]> {
    return authorizedAbortableFetchAsJson(`${ApiService.v1Url}/players/${playerId}/trips`, this.getAccessToken);
  }

  getComps(playerId: string): IAbortablePromise<IPlayerComp[]> {
    return authorizedAbortableFetchAsJson(`${ApiService.v1Url}/players/${playerId}/comps`, this.getAccessToken);
  }

  getOffers(playerId: string, categoryIds: number[], statuses: string[]): IAbortablePromise<IPlayerOffer[]> {
    const url = new URL(`${ApiService.v1Url}/players/${playerId}/offers`);
    const params = new URLSearchParams();
    categoryIds.forEach((categoryId) => {
      params.append("categoryIds", categoryId.toString());
    });
    statuses.forEach((status) => {
      params.append("statuses", status);
    });
    url.search = params.toString();
    return authorizedAbortableFetchAsJson(url, this.getAccessToken);
  }

  getCreditTransactions(playerId: string): IAbortablePromise<IPlayerCreditTransaction[]> {
    return authorizedAbortableFetchAsJson(`${ApiService.v1Url}/players/${playerId}/credit`, this.getAccessToken);
  }

  updateEstimatedArrivalTime(playerId: string, bookingId: string, time: string): Promise<Response> {
    return apiNotConnected();
  }

  updateSourceOfFunds(playerId: string, sourceOfFunds: IPlayerSourceOfFunds): Promise<Response> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/players/${playerId}/employment`, this.getAccessToken, {
      method: "PUT",
      body: JSON.stringify(sourceOfFunds),
      headers: { "Content-Type": "application/json" },
    });
  }

  bookHotelBooking(bookingId: string): Promise<Response> {
    return authorizedFetch(`${ApiService.v1Url}/hotel/booking/${bookingId}/book`, this.getAccessToken, {
      method: "POST",
    });
  }

  getReservationsByStatus(
    playerId: string | number,
    status: "current" | "upcoming" | "cancelled" | "previous"
  ): IAbortablePromise<IBasicReservation[]> {
    return authorizedAbortableFetchAsJson(
      `${ApiService.v1Url}/players/${playerId}/reservations/status/${status}`,
      this.getAccessToken
    );
  }
  getReservation(playerId: string | number, reservationId: string): Promise<IReservation> {
    return authorizedFetchAsJson(
      `${ApiService.v1Url}/players/${playerId}/reservations/${reservationId}`,
      this.getAccessToken
    );
  }
  cancelReservation(confirmationNumber: string | number): Promise<Response> {
    return apiNotConnected();
  }

  getFolios(playerId: string | number, confirmationNumber: string | number): Promise<IPlayerFolio[]> {
    return authorizedFetchAsJson(
      `${ApiService.v1Url}/players/${playerId}/reservations/${confirmationNumber}/folios`,
      this.getAccessToken
    );
  }

  getWinLossYears(playerId: string | number): Promise<number[]> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/players/${playerId}/winloss/years`, this.getAccessToken);
  }

  getWinLossFor(playerId: string | number, winLossYear: string | number): Promise<Blob> {
    return authorizedFetch(`${ApiService.v1Url}/players/${playerId}/winloss/${winLossYear}`, this.getAccessToken).then(
      (r) => r.blob()
    );
  }

  getMobileAppInformation(playerId: string | number): Promise<IPlayerMobileAppInformation> {
    return authorizedFetchAsJson(`${ApiService.v1Url}/players/${playerId}/app/info`, this.getAccessToken);
  }

  unlinkUser(playerId: string | number): Promise<Response> {
    return authorizedFetch(`${ApiService.v1Url}/players/${playerId}/club-serrano-online/unlink`, this.getAccessToken, {
      method: "DELETE",
    });
  }

  resetPassword(playerId: string | number): Promise<Response> {
    return authorizedFetch(
      `${ApiService.v1Url}/players/${playerId}/club-serrano-online/reset-password`,
      this.getAccessToken,
      {
        method: "POST",
      }
    );
  }

  updateEmail(playerId: string | number, newEmail: string): Promise<Response> {
    return authorizedFetch(`${ApiService.v1Url}/players/${playerId}/club-serrano-online/email`, this.getAccessToken, {
      method: "PUT",
      body: JSON.stringify({ newEmailAddress: newEmail }),
      headers: { "Content-Type": "application/json" },
    });
  }

  getClubInfo(playerId: string | number): Promise<IClubSerranoInfo> {
    return authorizedFetchAsJson(
      `${ApiService.v1Url}/players/${playerId}/club-serrano-online/info`,
      this.getAccessToken
    );
  }
}
